import React from "react";
import AnimationRevealPage from "helpers/AnimationRevealPage.js";
import {useParams} from "react-router-dom";
import {Container} from "components/misc/Layouts";
import tw from "twin.macro";
import Header from "components/headers/light.js";
import Footer from "components/footers/FiveColumnWithInputForm.js";

import studyList from "../studyList.json";
import styled from "styled-components";
import {Helmet} from "react-helmet";

const ContentWithPaddingLg = tw.div`max-w-screen-xl mx-auto py-8 lg:py-16`;
const HeadingRow = tw.div`flex flex-col md:flex-row md:items-center md:justify-between gap-4`;
const Heading = tw.h2`text-2xl font-black tracking-wide text-gray-900`;
const LogoContainer = tw.div`flex items-center gap-4 justify-center md:justify-end`;
const Logo = tw.img`h-12 w-auto object-contain`;

const Description = styled.div`
    ${tw`mt-6 text-left text-sm md:text-base lg:text-lg font-medium leading-relaxed text-secondary-100`}
    p {
        font-size: 1rem;
    }
    li {
        font-size: 1rem;
    }
    h2 {
        font-size: 1.25rem;
    }
    ol,
    ul {
        padding-top: 10px;
        list-style-type: disc;
        margin-left: 20px;
    }
    li span {
        font-size: 1.125rem;
    }
`;

const ImageGrid = tw.div`mt-10 grid grid-cols-1 max-w-xl mx-auto gap-8`;

const GridImage = styled.img`
  ${tw`w-full h-full rounded-lg shadow-lg`}
`;

export default () => {
    const { slug } = useParams();

    const post = studyList.find(a => a.slug === slug);

    if (!post) {
        return (
            <HeadingRow>
                <Heading>404 Study Paper not found</Heading>
            </HeadingRow>
        );
    }

    return (
        <>
            <Helmet>
                <title>{post.title} | MAI Motion Clinical Study</title>
                <meta name="description" content={post.shortDescription} />
                <meta name="keywords" content="clinical study, motion analysis, healthcare research, biomechanics, MAI Motion study" />
                <link rel="canonical" href={`https://maimotion.com/study/${slug}`} />
                <meta property="og:title" content={`${post.title} | MAI Motion Clinical Study`} />
                <meta property="og:description" content={post.shortDescription} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={`https://maimotion.com/study/${slug}`} />
                <meta property="og:image" content={post.image} />
                <meta name="twitter:title" content={`${post.title} | MAI Motion Clinical Study`} />
                <meta name="twitter:description" content={post.shortDescription} />
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:image" content={post.image} />
                <meta name="robots" content="index,follow" />
                <meta name="author" content="MAI Motion Clinical Team" />
                <meta name="article:published_time" content={post.date} />
                <meta name="article:section" content={post.group} />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "MedicalStudy",
                        "name": post.title,
                        "description": post.shortDescription,
                        "image": post.image,
                        "datePublished": post.date,
                        "url": `https://maimotion.com/study/${slug}`,
                        "studyType": post.group,
                        "publisher": {
                            "@type": "Organization",
                            "name": "MAI Motion",
                            "url": "https://maimotion.com",
                            "logo": {
                                "@type": "ImageObject",
                                "url": "https://maimotion.com/logo512.png"
                            }
                        },
                        "mainEntityOfPage": {
                            "@type": "WebPage",
                            "@id": `https://maimotion.com/study/${slug}`
                        },
                        "about": [
                            "Clinical Study",
                            "Motion Analysis",
                            "Healthcare Research",
                            "Biomechanics"
                        ]
                    })}
                </script>
            </Helmet>
            <AnimationRevealPage>
                <Header />
                <Container>
                    <ContentWithPaddingLg>
                        <HeadingRow>
                            <Heading style={{ color: "#c7942d" }}>{post.title}</Heading>
                            <LogoContainer>
                                <Logo src={require("../images/incrediwear.jpeg")} alt="Incrediwear Logo" />
                                <Logo src={require("../images/msk-doctors.png")} alt="MSK Doctors Logo" />
                            </LogoContainer>
                        </HeadingRow>

                            <Description>
                                <div dangerouslySetInnerHTML={{ __html: post.authors }} />
                            </Description>

                        {/* Image Grid - only show for non-Achilles studies */}
                        {post.images && post.images.length > 0 && (
                            <ImageGrid>
                                {post.images.map((src, index) => (
                                    <GridImage
                                        key={index}
                                        src={"https://d3ekn1do43nbxb.cloudfront.net/studies/images/"+ src}
                                        alt={`Study ${index + 1}`}
                                    />
                                ))}
                            </ImageGrid>
                        )}
                        <Description style={{ marginTop: 50 }}>
                            <div dangerouslySetInnerHTML={{ __html: post.html }} />
                        </Description>
                    </ContentWithPaddingLg>
                </Container>
                <Footer />
            </AnimationRevealPage>
        </>
    );
};
